import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { VideoConfig } from 'src/app/core/models/contract';

@Component({
  selector: 'app-signature-video-player',
  templateUrl: './signature-video-player.component.html',
  styleUrls: ['./signature-video-player.component.scss'],
})
export class SignatureVideoPlayerComponent implements OnInit, OnDestroy {
  @Input() config!: VideoConfig;
  @Input() startTime = 0;
  @Output() videoCompleted = new EventEmitter<void>();
  @Output() videoStarted = new EventEmitter<void>();
  @Output() videoPaused = new EventEmitter<void>();
  @Output() videoResumed = new EventEmitter<void>();

  @ViewChild('videoElement', { static: true }) videoRef!: ElementRef<HTMLVideoElement>;

  isPlaying = false;
  isMuted = false;
  progress = 0;
  currentTime = 0;
  duration = 0;
  completed = false;

  /** Available playback speeds — capped at 1.5x per Mishcon approval */
  readonly playbackRates = [1, 1.25, 1.5];
  playbackRate = 1;

  /** Highest timestamp the user has actually watched to (anti-skip) */
  private maxWatchedTime = 0;

  ngOnInit(): void {
    const video = this.videoRef.nativeElement;

    video.addEventListener('play', this.onPlay);
    video.addEventListener('pause', this.onPause);
    video.addEventListener('timeupdate', this.onTimeUpdate);
    video.addEventListener('ended', this.onEnded);
    video.addEventListener('seeking', this.onSeeking);
    video.addEventListener('loadedmetadata', this.onLoadedMetadata);

    if (this.config.autoplay) {
      video.muted = true;
      this.isMuted = true;
      video.play().catch(() => {
        // Autoplay blocked by browser — user will click play
      });
    }
  }

  ngOnDestroy(): void {
    const video = this.videoRef.nativeElement;
    video.removeEventListener('play', this.onPlay);
    video.removeEventListener('pause', this.onPause);
    video.removeEventListener('timeupdate', this.onTimeUpdate);
    video.removeEventListener('ended', this.onEnded);
    video.removeEventListener('seeking', this.onSeeking);
    video.removeEventListener('loadedmetadata', this.onLoadedMetadata);
  }

  togglePlay(): void {
    const video = this.videoRef.nativeElement;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }

  toggleMute(): void {
    const video = this.videoRef.nativeElement;
    video.muted = !video.muted;
    this.isMuted = video.muted;
  }

  /** Cycle through the allowed playback speeds (1x → 1.25x → 1.5x → 1x). */
  cyclePlaybackRate(): void {
    const currentIndex = this.playbackRates.indexOf(this.playbackRate);
    const nextIndex = (currentIndex + 1) % this.playbackRates.length;
    this.playbackRate = this.playbackRates[nextIndex];
    this.videoRef.nativeElement.playbackRate = this.playbackRate;
  }

  // --- Event handlers (arrow functions to preserve `this`) ---

  private isFirstPlay = true;

  private onPlay = (): void => {
    this.isPlaying = true;
    if (this.isFirstPlay) {
      this.isFirstPlay = false;
      this.videoStarted.emit();
    } else {
      this.videoResumed.emit();
    }
  };

  private onPause = (): void => {
    this.isPlaying = false;
    this.videoPaused.emit();
  };

  private onLoadedMetadata = (): void => {
    const video = this.videoRef.nativeElement;
    this.duration = video.duration;
    video.playbackRate = this.playbackRate;
    if (this.startTime > 0 && this.startTime < this.duration) {
      video.currentTime = this.startTime;
      this.maxWatchedTime = this.startTime;
    }
  };

  private onTimeUpdate = (): void => {
    const video = this.videoRef.nativeElement;
    this.currentTime = video.currentTime;

    if (video.currentTime > this.maxWatchedTime) {
      this.maxWatchedTime = video.currentTime;
    }

    if (this.duration > 0) {
      this.progress = (video.currentTime / this.duration) * 100;
    }
  };

  private onSeeking = (): void => {
    const video = this.videoRef.nativeElement;
    // Block seeking ahead of what was actually watched
    if (this.config.unskippable && video.currentTime > this.maxWatchedTime + 1) {
      video.currentTime = this.maxWatchedTime;
    }
  };

  private onEnded = (): void => {
    this.isPlaying = false;
    this.completed = true;
    this.progress = 100;
    this.videoCompleted.emit();
  };

  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
}
